<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ABOUT_IMAGES } from '@/assets/marketing/about/images'
import { useRevealOnScroll } from '@/composables/useRevealOnScroll'

const props = withDefaults(defineProps<{ variant?: 'public' | 'dashboard' }>(), { variant: 'public' })

const { t } = useI18n()
const { target, revealed } = useRevealOnScroll()

const milestones = ['m1', 'm2', 'm3', 'm4'] as const
const STAGGER = ['', 'motion-safe:delay-100', 'motion-safe:delay-200', 'motion-safe:delay-300'] as const
const isDashboard = computed(() => props.variant === 'dashboard')
</script>

<template>
  <section
    id="story"
    :class="isDashboard ? 'rounded-2xl bg-slate-800 border border-slate-700 p-8' : 'bg-white py-24'"
    aria-labelledby="about-story-title"
  >
    <div ref="target" :class="isDashboard ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div
          class="rounded-xl overflow-hidden shadow-2xl border"
          :class="isDashboard ? 'border-slate-700' : 'border-slate-200'"
        >
          <img
            :src="ABOUT_IMAGES.story.src"
            :width="ABOUT_IMAGES.story.width"
            :height="ABOUT_IMAGES.story.height"
            :alt="t('about.images.story_alt')"
            loading="lazy"
            class="w-full h-auto"
          />
        </div>
        <div>
          <p class="font-semibold text-sm uppercase tracking-widest" :class="isDashboard ? 'text-emerald-400' : 'text-emerald-600'">
            {{ t('about.story.eyebrow') }}
          </p>
          <h2
            id="about-story-title"
            tabindex="-1"
            class="text-3xl md:text-4xl font-bold mt-3 focus:outline-none"
            :class="isDashboard ? 'text-white' : 'text-slate-900'"
          >
            {{ t('about.story.title') }}
          </h2>
          <p class="mt-5 leading-relaxed" :class="isDashboard ? 'text-slate-400' : 'text-slate-600'">{{ t('about.story.p1') }}</p>
          <p class="mt-4 leading-relaxed" :class="isDashboard ? 'text-slate-400' : 'text-slate-600'">{{ t('about.story.p2') }}</p>
          <p class="mt-4 leading-relaxed" :class="isDashboard ? 'text-slate-400' : 'text-slate-600'">{{ t('about.story.p3') }}</p>
        </div>
      </div>

      <ol class="mt-16 grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-6 relative" :aria-label="t('about.story.timeline_label')">
        <li
          v-for="(key, index) in milestones"
          :key="key"
          class="relative pl-8 md:pl-0 md:pt-8 motion-safe:transition-all motion-safe:duration-500"
          :class="[revealed ? '' : 'motion-safe:opacity-0 motion-safe:translate-y-4', STAGGER[index]]"
        >
          <span
            class="absolute left-0 top-1.5 md:top-0 w-3 h-3 rounded-full bg-emerald-500"
            aria-hidden="true"
          ></span>
          <span
            class="absolute left-[5px] top-4 bottom-[-2rem] w-px md:left-3 md:top-[5px] md:bottom-auto md:h-px md:w-[calc(100%+1.5rem)]"
            :class="[
              isDashboard ? 'bg-slate-600' : 'bg-slate-200',
              index === milestones.length - 1 ? 'hidden' : '',
            ]"
            aria-hidden="true"
          ></span>
          <p class="text-sm font-bold" :class="isDashboard ? 'text-white' : 'text-slate-900'">
            {{ t(`about.story.milestones.${key}.label`) }}
          </p>
          <p class="mt-1 text-sm leading-relaxed" :class="isDashboard ? 'text-slate-400' : 'text-slate-600'">
            {{ t(`about.story.milestones.${key}.text`) }}
          </p>
        </li>
      </ol>
    </div>
  </section>
</template>
